import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MapPin } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AddressAutocomplete = ({ value, onChange, placeholder, className, 'data-testid': testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setInputValue(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = useCallback(async (query) => {
    if (!query || query.length < 3) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/address/search`, { params: { q: query, limit: 5 }, withCredentials: true });
      setSuggestions(res.data);
      setShowDropdown(res.data.length > 0);
    } catch (e) { setSuggestions([]); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val, null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAddress(val), 300);
  };

  const handleSelect = (suggestion) => {
    setInputValue(suggestion.label);
    setShowDropdown(false);
    setSuggestions([]);
    onChange(suggestion.label, { lat: suggestion.lat, lon: suggestion.lon, city: suggestion.city, postcode: suggestion.postcode });
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <MapPin size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#5E5F66', pointerEvents: 'none' }} />
        <input
          data-testid={testId || 'address-autocomplete'}
          type="text"
          className={className || 'form-input'}
          value={inputValue}
          onChange={handleChange}
          onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
          placeholder={placeholder || 'Rechercher une adresse...'}
          style={{ paddingLeft: '32px' }}
          autoComplete="off"
        />
      </div>
      {showDropdown && suggestions.length > 0 && (
        <div data-testid="address-suggestions" style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          backgroundColor: 'white', border: '1px solid #E2E2E6', borderRadius: '0 0 6px 6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto'
        }}>
          {suggestions.map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              data-testid={`suggestion-${i}`}
              onClick={() => handleSelect(s)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px',
                border: 'none', cursor: 'pointer', backgroundColor: 'transparent', width: '100%',
                textAlign: 'left', borderBottom: i < suggestions.length - 1 ? '1px solid #F4F5F7' : 'none',
                fontSize: '0.85rem', transition: 'background-color 0.1s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <MapPin size={14} style={{ color: '#C62828', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: '600' }}>{s.label}</div>
                <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{s.context}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
